import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Check, X, FileText, ExternalLink } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const STATUS_LABEL = {
  created: "Создан", verifying: "Проверка", confirmed: "Подтверждён",
  manual_review: "Ручная проверка", rejected: "Отклонён", expired: "Истёк", refunded: "Возврат",
};
const STATUS_COLOR = {
  created: "bg-muted text-foreground/70",
  verifying: "bg-blue-500/15 text-blue-700",
  confirmed: "bg-green-600/15 text-green-700",
  manual_review: "bg-amber-500/20 text-amber-800",
  rejected: "bg-red-500/15 text-red-700",
};
const REASON_LABEL = {
  qr_not_found: "QR не распознан",
  kaspi_unavailable: "Kaspi недоступен",
  parse_failed: "Не удалось прочитать чек",
  seller_mismatch: "Чужой продавец",
  receipt_already_used: "Чек уже использован",
  underpaid: "Недоплата",
  manual_reject: "Отклонён вручную",
};
const FILTERS = ["", "manual_review", "confirmed", "rejected", "created"];

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("manual_review");
  const [loading, setLoading] = useState(true);
  const [amounts, setAmounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payments${filter ? `?status=${filter}` : ""}`);
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const fd = new FormData();
    if (action === "confirm" && amounts[id]) fd.append("amount", amounts[id]);
    if (action === "reject") fd.append("reason", "manual_reject");
    await api.post(`/admin/payments/${id}/${action}`, fd);
    load();
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-3xl">Оплаты Kaspi</h1>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="payments-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Обновить
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f || "all"}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm border ${filter === f ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}
            data-testid={`payments-filter-${f || "all"}`}
          >
            {f ? STATUS_LABEL[f] : "Все"}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Загрузка...</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="payments-empty">Заявок нет</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-card border border-border p-4 space-y-3" data-testid={`payment-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium">{p.clientName || "Без имени"} · {p.clientPhone || "—"}</p>
                  <p className="text-sm text-muted-foreground">
                    {p.venueName} · {p.bookingDate} · бронь {formatPrice(p.bookingTotalKzt || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Требуется: {formatPrice(p.requestedAmountKzt)}
                    {p.actualAmountKzt ? ` · Оплачено: ${formatPrice(p.actualAmountKzt)}` : ""}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  <span className={`inline-block px-2 py-1 text-xs ${STATUS_COLOR[p.status] || "bg-muted"}`}>
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                  {p.reviewReason && (
                    <p className="text-xs text-muted-foreground">{REASON_LABEL[p.reviewReason] || p.reviewReason}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 flex-wrap text-xs text-muted-foreground">
                {p.kaspiRnm && <span>РНМ: {p.kaspiRnm}</span>}
                {p.kaspiFiscalSign && <span>ФП: {p.kaspiFiscalSign}</span>}
                {p.kaspiExtTranId && <span>Чек: {p.kaspiExtTranId}</span>}
                {p.kaspiSaleAt && <span>{p.kaspiSaleAt}</span>}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {p.receiptStoragePath && (
                  <a href={`${backend}/api/files/${p.receiptStoragePath}`} target="_blank" rel="noreferrer"
                     className="text-sm text-primary inline-flex items-center gap-1 underline"
                     data-testid={`payment-file-${p.id}`}>
                    <FileText className="w-4 h-4" /> Файл чека
                  </a>
                )}
                {p.receiptUrl && (
                  <a href={p.receiptUrl} target="_blank" rel="noreferrer"
                     className="text-sm text-primary inline-flex items-center gap-1 underline"
                     data-testid={`payment-url-${p.id}`}>
                    <ExternalLink className="w-4 h-4" /> Чек Kaspi
                  </a>
                )}
              </div>

              {p.status !== "confirmed" && (
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
                  <Input
                    type="number"
                    placeholder="Сумма, тг"
                    value={amounts[p.id] || ""}
                    onChange={(e) => setAmounts({ ...amounts, [p.id]: e.target.value })}
                    className="w-36 h-9"
                    data-testid={`payment-amount-input-${p.id}`}
                  />
                  <Button size="sm" onClick={() => act(p.id, "confirm")} className="gap-1 bg-primary" data-testid={`payment-confirm-${p.id}`}>
                    <Check className="w-4 h-4" /> Подтвердить
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act(p.id, "reject")} className="gap-1 text-destructive" data-testid={`payment-reject-${p.id}`}>
                    <X className="w-4 h-4" /> Отклонить
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
